/**
 * Compact dashboard widget summarising the user's human review requests.
 *
 * Hidden when the user has no requests. Shows an unread indicator when a newly
 * completed review hasn't been opened. Links to the My Human Reviews page.
 *
 * @module HumanReviewsWidget
 */
import { Link } from 'react-router-dom';
import { ArrowRight, UserCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { useReviewSummary } from '@/hooks/useHumanReviews';
import { countUnread, useUnreadNotifications } from '@/hooks/useNotifications';
import { NOTIFICATION_HUMAN_REVIEW_COMPLETED } from '@/lib/notificationsApi';
import { ROUTES } from '@/lib/routeConstants';

export function HumanReviewsWidget() {
  const { t } = useTranslation();
  const { summary } = useReviewSummary();
  const { notifications } = useUnreadNotifications();

  // Additive widget: render nothing until the user has at least one request.
  if (!summary || summary.totalCount === 0) {
    return null;
  }

  const hasUnread = countUnread(notifications, NOTIFICATION_HUMAN_REVIEW_COMPLETED) > 0;

  return (
    <Card className="border-border">
      <CardContent className="p-4">
        <Link
          to={ROUTES.HUMAN_REVIEWS}
          className="flex items-center justify-between gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          <div className="flex items-center gap-3">
            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <UserCheck className="h-4 w-4" aria-hidden />
              {hasUnread && (
                <span
                  className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-background bg-destructive"
                  aria-hidden
                />
              )}
            </span>
            <div className="min-w-0">
              <p className="font-ui flex items-center gap-2 text-sm font-semibold text-foreground">
                {t('humanReview.widget.title')}
                {hasUnread && (
                  <span className="font-ui rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold text-destructive">
                    {t('humanReview.widget.unread')}
                  </span>
                )}
              </p>
              <p className="font-ui text-xs text-muted-foreground">
                {t('humanReview.widget.summary', {
                  inProgress: summary.inProgressCount,
                  completed: summary.completedCount,
                })}
              </p>
            </div>
          </div>
          <span className="font-ui inline-flex items-center gap-1 text-xs font-medium text-primary">
            {t('humanReview.widget.viewAll')}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </span>
        </Link>
      </CardContent>
    </Card>
  );
}
